import {Avatar, Button, DataStatus, Separator} from 'platform/components';
import {Box, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {Fragment} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CheckoutPaymentIntegrationName,
  CheckoutPaymentIntegrationState,
  useGetCheckoutIntegrationsQuery,
  usePostCheckoutPaymentIntegrationRerunMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {EMPTY_PLACEHOLDER, Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

// Product names of the external services; they are not translated.
const INTEGRATION_NAMES: Record<CheckoutPaymentIntegrationName, string> = {
  CEBIA: 'Cebia',
  BOSCH_FLEET: 'Bosch Fleet',
  HYUNDAI_CRM_EU: 'Hyundai CRM EU',
  HYUNDAI_EHISTORIE: 'Hyundai eHistorie',
};

type NamedIntegrationState = CheckoutPaymentIntegrationState & {
  integration: CheckoutPaymentIntegrationName;
};

const isNamedIntegration = (row: CheckoutPaymentIntegrationState): row is NamedIntegrationState =>
  isNotNil(row.integration);

// A row without a state has not finished, so it must not look like a success.
const getStateAvatar = (state: CheckoutPaymentIntegrationState['state']) =>
  match(state)
    .with(
      'failure',
      () =>
        ({icon: 'alert/error', color: 'palettes.red.10.100', iconColor: 'severity.danger'}) as const
    )
    .with(
      'success',
      () =>
        ({
          icon: 'action/check_circle',
          color: 'palettes.green.10.100',
          iconColor: 'severity.success',
        }) as const
    )
    .otherwise(
      () =>
        ({
          icon: 'action/hourglass_empty',
          color: 'palettes.neutral.20.100',
          iconColor: 'text.secondary',
        }) as const
    );

interface PaymentIntegrationsProps extends RequiredTestIdProps {
  checkoutId: string;
  paymentId: string;
}

export function PaymentIntegrations(props: PaymentIntegrationsProps) {
  const formatDateTime = useDateTimeFormatter();

  // Integrations finish asynchronously after issuing, so each opening of the view loads them again.
  const {data, isLoading, isError} = useGetCheckoutIntegrationsQuery(
    {checkoutId: props.checkoutId, paymentIds: [props.paymentId]},
    {refetchOnMountOrArgChange: true}
  );

  const [rerunIntegration, {isLoading: isRerunning, originalArgs: rerunArgs}] =
    usePostCheckoutPaymentIntegrationRerunMutation();

  const handleRerun = (integration: CheckoutPaymentIntegrationName) =>
    rerunIntegration({
      checkoutId: props.checkoutId,
      paymentId: props.paymentId,
      body: {integration},
    })
      .unwrap()
      .catch(handleApiError);

  const formatAttemptedAt = (attemptedAt: string | Nullish) => {
    const date = parseDate(attemptedAt);

    return isNil(date) ? null : formatDateTime('dateTimeShort', date);
  };

  // A row without an integration name cannot be labelled or rerun, so it is not shown.
  const namedIntegrations =
    data?.flatMap((payment) => payment.integrations).filter(isNamedIntegration) ?? [];

  return (
    <DataStatus
      isLoading={isLoading}
      isError={isError}
      isEmpty={namedIntegrations.length === 0}
      spacing={5}
    >
      <VStack>
        {namedIntegrations.map((row, index) => {
          const isFailure = row.state === 'failure';
          const stateAvatar = getStateAvatar(row.state);
          const attemptedAt = formatAttemptedAt(row.attemptedAt);

          return (
            <Fragment key={row.integration}>
              <Show when={index > 0}>
                <Separator />
              </Show>
              <HStack
                spacing={3}
                align="center"
                data-testid={suffixTestId(`integration-${row.integration}`, props)}
              >
                <Avatar
                  size="default"
                  icon={stateAvatar.icon}
                  color={stateAvatar.color}
                  iconColor={stateAvatar.iconColor}
                />
                <VStack spacing={1}>
                  <Text size="xSmall" color={isFailure ? 'danger' : 'secondary'}>
                    {INTEGRATION_NAMES[row.integration]}
                  </Text>
                  <Text color={isFailure ? 'danger' : undefined}>
                    {row.message ?? EMPTY_PLACEHOLDER}
                  </Text>
                </VStack>
                <Space fillAvailable />
                <Show when={isNotNilOrEmpty(attemptedAt) ? attemptedAt : null}>
                  {(attemptedAt) => (
                    <Text size="xSmall" color="secondary">
                      {attemptedAt}
                    </Text>
                  )}
                </Show>
                <Show when={isFailure && row.rerunAllowed === true}>
                  <Box>
                    <Button
                      variant="link"
                      leftIcon="navigation/refresh"
                      title={i18n.t('general.actions.tryAgain')}
                      onClick={() => handleRerun(row.integration)}
                      isLoading={isRerunning && rerunArgs?.body.integration === row.integration}
                      isDisabled={isRerunning}
                      data-testid={suffixTestId(`integration-retry-${row.integration}`, props)}
                    />
                  </Box>
                </Show>
              </HStack>
            </Fragment>
          );
        })}
      </VStack>
    </DataStatus>
  );
}
